import { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { corrupt, err, ok } from "@pack-it-up/shared";
// --- the only place Firestore `Timestamp` is understood ---
// zod schema that accepts a Firestore Timestamp and yields a JS Date.
export const firestoreTimestamp = () => z
    .custom((v) => v instanceof Timestamp, { message: "expected Firestore Timestamp" })
    .transform((t) => t.toDate());
export const toTimestamp = (d) => Timestamp.fromDate(d);
// Parse a raw Firestore document against its DTO schema; a parse failure becomes a
// DomainError.Corrupt (never a raw zod throw — T10 §2.3). The output type is inferred
// from the schema (so a Timestamp-input field still yields a Date-typed result).
export const parseDto = (schema, raw, entity) => {
    const r = schema.safeParse(raw);
    if (r.success)
        return ok(r.data);
    return err(corrupt(`${entity}: ${r.error.issues.map((i) => i.message).join(", ")}`));
};
// CalendarDate is always a plain 'YYYY-MM-DD' string — never a Timestamp (Doc 02 §3).
export const calendarDate = () => z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const RoleSchema = z.enum(["owner", "editor", "viewer"]);
export const VisibilitySchema = z.enum(["private", "link", "public"]);
export const RelativeOffsetSchema = z.object({
    value: z.number(),
    unit: z.enum(["day", "week", "month", "year"]),
});
// --- assignment (plain JSON, no dates) ---
// One shared `Assignment` type for both packing items and todos (item.mapper +
// todo.mapper reuse this). `specific` carries per-allocation `quantity`, which is
// meaningful only for packing items; on a todo the allocations still name the
// assigned participants (assignment drives todo filtering, Doc 02 §5) but the
// per-allocation quantity is unused.
export const AssignmentSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("everyone") }),
    z.object({ kind: z.literal("shared") }),
    z.object({
        kind: z.literal("specific"),
        allocations: z.array(z.object({ participantId: z.string(), quantity: z.number() })),
    }),
]);
export const assignmentToDomain = (a) => a;
export const assignmentToDto = (a) => a;
// --- completion map (dates -> Timestamp) ---
// One persistent per-participant record (assignment is a view over it, not part of the
// stored shape — see core/usecases/completion.ts).
export const CompletionSchema = z.object({
    byParticipant: z.record(z.string(), z.object({ done: z.boolean(), checkedBy: z.string().optional(), at: firestoreTimestamp().optional() })),
});
export const completionToDomain = (c) => {
    const byParticipant = {};
    for (const [pid, e] of Object.entries(c.byParticipant)) {
        byParticipant[pid] = {
            done: e.done,
            ...(e.checkedBy !== undefined ? { checkedBy: e.checkedBy } : {}),
            ...(e.at !== undefined ? { at: e.at } : {}),
        };
    }
    return { byParticipant };
};
export const completionToDto = (c) => {
    const byParticipant = {};
    for (const [pid, e] of Object.entries(c.byParticipant)) {
        byParticipant[pid] = {
            done: e.done,
            ...(e.checkedBy !== undefined ? { checkedBy: e.checkedBy } : {}),
            ...(e.at !== undefined ? { at: toTimestamp(e.at) } : {}),
        };
    }
    return { byParticipant };
};
// --- item snapshot + provenance (plain JSON) ---
export const ItemSnapshotSchema = z.object({
    name: z.string(),
    quantity: z.number().optional(),
    description: z.string().optional(),
    notes: z.string().optional(),
    tags: z.array(z.string()).optional(),
    assignment: AssignmentSchema,
    relativeDueOffset: RelativeOffsetSchema.optional(),
});
const snapshotToDomain = (s) => ({
    name: s.name,
    ...(s.quantity !== undefined ? { quantity: s.quantity } : {}),
    ...(s.description !== undefined ? { description: s.description } : {}),
    ...(s.notes !== undefined ? { notes: s.notes } : {}),
    ...(s.tags !== undefined ? { tags: s.tags } : {}),
    assignment: assignmentToDomain(s.assignment),
    ...(s.relativeDueOffset !== undefined ? { relativeDueOffset: s.relativeDueOffset } : {}),
});
const snapshotToDto = (s) => ({
    name: s.name,
    ...(s.quantity !== undefined ? { quantity: s.quantity } : {}),
    ...(s.description !== undefined ? { description: s.description } : {}),
    ...(s.notes !== undefined ? { notes: s.notes } : {}),
    ...(s.tags !== undefined ? { tags: s.tags } : {}),
    assignment: assignmentToDto(s.assignment),
    ...(s.relativeDueOffset !== undefined ? { relativeDueOffset: s.relativeDueOffset } : {}),
});
export const ProvenanceSchema = z.object({
    templateSources: z.array(z.object({ templateId: z.string(), templateItemId: z.string(), templateName: z.string().optional() })),
    createdByImport: z.boolean(),
    importedSnapshot: ItemSnapshotSchema,
    modifiedSinceImport: z.boolean(),
});
export const provenanceToDomain = (p) => ({
    templateSources: p.templateSources.map((s) => ({
        templateId: s.templateId,
        templateItemId: s.templateItemId,
        ...(s.templateName !== undefined ? { templateName: s.templateName } : {}),
    })),
    createdByImport: p.createdByImport,
    importedSnapshot: snapshotToDomain(p.importedSnapshot),
    modifiedSinceImport: p.modifiedSinceImport,
});
export const provenanceToDto = (p) => ({
    templateSources: p.templateSources.map((s) => ({
        templateId: s.templateId,
        templateItemId: s.templateItemId,
        ...(s.templateName !== undefined ? { templateName: s.templateName } : {}),
    })),
    createdByImport: p.createdByImport,
    importedSnapshot: snapshotToDto(p.importedSnapshot),
    modifiedSinceImport: p.modifiedSinceImport,
});
//# sourceMappingURL=common.js.map